import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { StateService, UIRouterGlobals } from '@uirouter/core';
import { AuthService } from '@obo-main/services/auth/auth.service';
import { Utils } from '@obo-main/utils/utils.service';
import { TourEvent, TourService } from '@obo-main/services/tour/tour.service';
import { Constants } from '@obo-app/constants';
import {
    ConfiguratorActionBarStep,
    ConfiguratorActionBarStepGroup
} from '@obo-main/components/configurator/configuratorActionBar/configuratorActionBarStep.model';
import { ConfiguratorActionBarService } from '@obo-main/services/configurator/configuratorActionBar.service';
import { AppSettings } from '@obo-main/utils/appSettings.service';
import { ChatService } from '@obo-main/components/chatBot/services/chat.service';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-configurator-action-bar',
    templateUrl: './configuratorActionBar.component.html',
    styleUrls: ['./configuratorActionBar.component.scss'],
    standalone: false
})
export class ConfiguratorActionBarComponent implements OnInit {
    public currentStep: ConfiguratorActionBarStep;
    public stepGroups: Array<ConfiguratorActionBarStepGroup>;
    private visitedSteps: Array<string>;
    protected isTourActive: boolean = false;
    protected showReview: false;

    constructor(
        private stateService: StateService,
        private configuratorActionBarService: ConfiguratorActionBarService,
        private authService: AuthService,
        private utils: Utils,
        private tourService: TourService,
        private cdr: ChangeDetectorRef,
        private appSettings: AppSettings,
        protected chatService: ChatService,
        private uiRouterGlobals: UIRouterGlobals
    ) {}

    ngOnInit(): void {
        this.showReview = this.appSettings.getItem('settings.enable_configurator_review');

        this.configuratorActionBarService.steps.subscribe((steps) => {
            this.visitedSteps = new Array<string>();
            this.initStepGroups(steps);
        });

        this.configuratorActionBarService.currentStep.subscribe((currentStep) => {
            this.currentStep = currentStep;
            if (currentStep) {
                if (!this.visitedSteps.includes(currentStep.name)) {
                    this.visitedSteps.push(currentStep.name);
                }
                this.updateStepGroupState();
            }
        });

        this.tourService.tourChange.subscribe((evt: TourEvent) => {
            this.isTourActive = !!evt;
            this.cdr.detectChanges();
        });
    }

    protected onEnterStepGroupButton($event: MouseEvent, stepGroup: ConfiguratorActionBarStepGroup): void {
        this.resetHoverState(stepGroup.completed ? stepGroup : undefined);
        firstValueFrom(
            this.configuratorActionBarService.showReview(
                stepGroup.steps[0].icon,
                stepGroup.steps[0].name,
                stepGroup.locked,
                stepGroup.completed,
                $event.target as HTMLElement
            )
        ).then(() => {
            this.resetHoverState();
        });
    }

    protected onLeaveStepGroupButton($event: MouseEvent, stepGroup: ConfiguratorActionBarStepGroup): void {
        if (stepGroup.completed) {
            const target = $event.target as HTMLElement;
            if (
                $event.offsetX < 0 ||
                ($event.offsetX < target.offsetWidth && ($event.offsetY < 0 || $event.offsetY >= target.offsetHeight))
            ) {
                this.configuratorActionBarService.closeReview();
                this.resetHoverState();
            }
        }
    }

    private resetHoverState(stepGroup?: ConfiguratorActionBarStepGroup): void {
        this.stepGroups.forEach((sG) => (sG.hovered = sG === stepGroup));
    }

    private isActive(stepGroup: ConfiguratorActionBarStepGroup): boolean {
        return this.hasCurrentStep(stepGroup);
    }

    private isLocked(stepGroup: ConfiguratorActionBarStepGroup): boolean {
        const isLockedFn = stepGroup.steps[0].locked;
        if (typeof isLockedFn === 'function') {
            return isLockedFn(this.uiRouterGlobals.transition);
        }
        return false;
    }

    protected cancel(): void {
        const projectId = this.configuratorActionBarService.projectId;
        if (this.isGuest) {
            this.stateService.go('portal');
        } else if (!projectId) {
            this.stateService.go('dashboard');
        } else {
            this.stateService.go('project', { projectId: projectId });
        }
    }

    private isCompleted(stepGroup: ConfiguratorActionBarStepGroup): boolean {
        const indexOfStepGroup = this.stepGroups.indexOf(stepGroup);
        const indexOfCurrentStepGroup = this.stepGroups.findIndex((sG) => this.hasCurrentStep(sG));
        return indexOfStepGroup < indexOfCurrentStepGroup;
    }

    private initStepGroups(steps: Array<ConfiguratorActionBarStep>): void {
        this.stepGroups = new Array<ConfiguratorActionBarStepGroup>();
        if (steps) {
            this.utils
                .groupBy('icon', steps)
                .map((group) => this.stepGroups.push(new ConfiguratorActionBarStepGroup(group.values)));
        }
    }

    private hasCurrentStep(stepGroup: ConfiguratorActionBarStepGroup): boolean {
        return stepGroup.steps.map((step) => step.name).includes(this.currentStep.name);
    }

    private get isGuest(): boolean {
        return this.authService.isInRole([Constants.Role.Guest]);
    }

    private updateStepGroupState(): void {
        this.stepGroups.forEach((stepGroup) => {
            stepGroup.active = this.isActive(stepGroup);
            stepGroup.locked = this.isLocked(stepGroup);
            stepGroup.completed = this.isCompleted(stepGroup);
        });
    }
}
